import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import type { FhirIdentifierMappingViewModel } from '../types';

export function DeleteMappingConfirmDialog({
  mapping,
  onOpenChange,
  onConfirm,
  pending,
  error,
}: {
  readonly mapping: FhirIdentifierMappingViewModel | null;
  readonly onOpenChange: (next: boolean) => void;
  readonly onConfirm: () => void;
  readonly pending: boolean;
  readonly error: unknown;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Dialog open={mapping !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('routes.admin.fhirIntegrations.mappings.delete.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.admin.fhirIntegrations.mappings.delete.body', {
              system: mapping?.fhirSystemValue ?? '',
            })}
          </DialogDescription>
        </DialogHeader>
        {error !== undefined && error !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.admin.fhirIntegrations.mappings.delete.error', {
              code: getErrorCodeForUi(error),
            })}
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={pending}
          >
            {t('routes.admin.fhirIntegrations.mappings.delete.cancel')}
          </Button>
          <Button type="button" variant="destructive" loading={pending} onClick={onConfirm}>
            {t('routes.admin.fhirIntegrations.mappings.delete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
